import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { formatBytes } from '../ui/bytes'
import {
  acceptShare,
  cancelShare,
  declineShare,
  deleteBackup,
  getAccountDevices,
  getBackups,
  getShares,
  getVault,
  BffError,
  type BackupRow,
} from '../transport/relay'
import { SidebarSection } from '../sidebar/SidebarSection'
import { useSections } from '../sidebar/useSections'
import { groupByMachine } from '../shell/machineGroups'
import { deviceDisplayName } from '../transport/deviceName'
import { onBackupFinished } from './backupJobs'
import { VaultRecover, VaultReset } from './VaultForms'
import { RestoreForm } from './RestoreForm'
import { ShareForm } from './ShareForm'
import { backupAge } from './backupWords'
import { StagePage, type StageChrome } from '../shell/StagePage'

/**
 * BACKUPS, in More — owner, 2026-09-29: 在 Artifact 以外, 我們還可以設置 Backup 的欄位.
 * 讓使用者可以瀏覽他有多少被 Backup 上去的 Agent, 知道他可以知道是什麼時候被 Backup 的.
 *
 * GROUPED BY MACHINE, THEN BY AGENT. The question asked is HOW MANY AGENTS are backed
 * up and WHEN each was; and the owner's follow-up the same day — Backup 也可以使用同樣
 * 的方式, 依照機器, 不同的機器分類把它收起來 — puts each machine in its own collapsible
 * group, exactly like Artifacts above and the Agents rail. Inside a machine, one row per
 * agent (how many backups, the newest's age); open it for every backup's time and size,
 * and to delete one.
 *
 * The list is the BFF's (records only — it cannot open a backup), so it answers even
 * when every machine is off; a backup belongs to the account, not to a machine that is up.
 *
 * A PAGE OF THE STAGE, AND AN AGENT A PAGE OF ITS OWN — owner, 2026-10-22: the rail holds
 * one button; this list is a page of the stage, and an agent opens in its place — every
 * backup's time and size, Restore, Share and Delete — with Back at the top left.
 */

type AgentGroup = { key: string; session: string; kind: string; rows: BackupRow[] }

/** A copy a friend shared is grouped by who shared it, not by a machine of theirs. */
const sharedKey = (r: BackupRow) => (r.shared_by ? `shared:${r.shared_by.email ?? r.shared_by.name ?? ''}` : '')

/** How many AGENTS have backups — what the page and the rail's Backups button count. */
export function backedUpAgents(rows: readonly BackupRow[]): number {
  return new Set(rows.map((r) => `${sharedKey(r) || r.device_id}|${r.device_id}:${r.session}`)).size
}

/** One machine's backups, by agent. */
function groupByAgent(rows: BackupRow[]): AgentGroup[] {
  const by = new Map<string, AgentGroup>()
  for (const r of rows) {
    const key = `${r.device_id}:${r.session}`
    const g = by.get(key) ?? { key, session: r.session, kind: r.kind, rows: [] }
    g.rows.push(r)
    by.set(key, g)
  }
  // Rows arrive newest first, so each group's first row is its newest, and groups
  // order by that.
  return [...by.values()].sort((a, b) => (b.rows[0]?.created_at ?? 0) - (a.rows[0]?.created_at ?? 0))
}

const when = (ts: number) => backupAge(ts)
const exactly = (ts: number) => new Date(ts * 1000).toLocaleString()

export function BackupsPage({
  onOpenAgent,
  ...chrome
}: StageChrome & {
  /** "Open it" on a finished restore: the new agent, on the machine it landed on. */
  onOpenAgent?: (a: { session: string; hash: string; device: string }) => void
}) {
  const qc = useQueryClient()
  const list = useQuery({ queryKey: ['backups'], queryFn: getBackups, staleTime: 0 })
  const vault = useQuery({ queryKey: ['backup-vault'], queryFn: getVault, staleTime: 30_000 })
  // The name the ACCOUNT gives each machine now (a rename shows here too), from the same
  // cached list the agent card reads; the hostname recorded at backup time otherwise.
  const devices = useQuery({ queryKey: ['menu-machines'], queryFn: getAccountDevices, staleTime: 30_000 })
  const { isOpen: machineOpen, toggle: toggleMachine } = useSections()
  useEffect(() => onBackupFinished(() => void qc.invalidateQueries({ queryKey: ['backups'] })), [qc])

  // The agent whose page is open, by its group key — or the list.
  const [open, setOpen] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  // Which backup's restore form is open — owner, 2026-09-29: 使用者點擊之後, 可以讓他選擇
  // 要用它複製到哪一個地方去.
  const [restoring, setRestoring] = useState<string | null>(null)
  // Which backup's share form is open — owner, 2026-09-30: 分享給他的好朋友.
  const [sharing, setSharing] = useState<string | null>(null)
  const shares = useQuery({ queryKey: ['backup-shares'], queryFn: getShares, staleTime: 0 })
  const [shareErr, setShareErr] = useState<Record<string, string>>({})
  const answer = useMutation({
    mutationFn: async ({ id, how }: { id: string; how: 'accept' | 'decline' | 'cancel' }) =>
      how === 'accept' ? acceptShare(id) : how === 'decline' ? declineShare(id) : cancelShare(id),
    onSuccess: (_r, v) => {
      setShareErr((e) => ({ ...e, [v.id]: '' }))
      void qc.invalidateQueries({ queryKey: ['backup-shares'] })
      if (v.how === 'accept') void qc.invalidateQueries({ queryKey: ['backups'] })
    },
    onError: (e, v) => {
      const msg = e instanceof BffError ? String((e.body as { message?: unknown }).message ?? e.message) : String(e)
      setShareErr((m) => ({ ...m, [v.id]: msg }))
      void qc.invalidateQueries({ queryKey: ['backup-shares'] })
    },
  })
  const incoming = shares.data?.incoming ?? []
  const outgoing = shares.data?.outgoing ?? []
  const [door, setDoor] = useState<null | 'recover' | 'reset'>(null)
  const [said, setSaid] = useState<string | null>(null)
  // A door opens at the bottom of a scrolling page; bring it into view, or the reset's
  // slide sits below the fold looking like nothing happened.
  const doorRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (door) doorRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [door])

  const del = useMutation({
    mutationFn: (id: string) => deleteBackup(id),
    onSuccess: () => {
      setConfirming(null)
      void qc.invalidateQueries({ queryKey: ['backups'] })
    },
  })

  const rows = useMemo(() => list.data?.backups ?? [], [list.data])
  const machines = useMemo(() => {
    const named = (id: string, recorded: string) => {
      const d = devices.data?.devices?.find((x) => x.device_id === id)
      return d ? deviceDisplayName(d) : recorded
    }
    return groupByMachine(
      rows,
      (r) => sharedKey(r) || r.device_id,
      (r) => (r.shared_by ? `Shared by ${r.shared_by.name || r.shared_by.email || 'a friend'}` : named(r.device_id, r.machine)),
    ).map((m) => ({
      ...m,
      agents: groupByAgent(m.rows),
    }))
  }, [rows, devices.data])
  const agentCount = backedUpAgents(rows)
  const policy = list.data?.policy
  const hasVault = !!vault.data?.vault

  /*
   * THE AGENT'S PAGE, while its group is in the list. Deleting its last backup takes the
   * group away, and the page with it: back to the list, not a page about nothing.
   */
  const openAt = machines.flatMap((m) => m.agents.map((g) => ({ m, g }))).find((x) => x.g.key === open)
  useEffect(() => {
    if (open && list.isSuccess && !openAt) setOpen(null)
  }, [open, list.isSuccess, openAt])
  const leaveAgent = () => {
    setOpen(null)
    setConfirming(null)
    setRestoring(null)
    setSharing(null)
  }

  if (openAt) {
    const { m, g } = openAt
    const ready = g.rows.filter((r) => r.state === 'ready')
    return (
      <StagePage
        {...chrome}
        onBack={leaveAgent}
        title={g.session}
        sub={`${m.name} · ${ready.length} backup${ready.length === 1 ? '' : 's'}`}
        backLabel="Back to Backups"
        className="more-page bk-page"
      >
        <ul className="bk-list">
          {g.rows.map((r) => (
            <li key={r.backup_id} className="bk-item">
              {confirming === r.backup_id ? (
                <>
                  <span className="bk-item-main">Delete the backup from {exactly(r.created_at)}? This cannot be undone.</span>
                  <span className="bk-item-acts">
                    <button type="button" className="sheet-btn danger" disabled={del.isPending} onClick={() => del.mutate(r.backup_id)}>
                      {del.isPending ? 'Deleting…' : 'Delete'}
                    </button>
                    <button type="button" className="sheet-btn" disabled={del.isPending} onClick={() => setConfirming(null)}>
                      Keep
                    </button>
                  </span>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="bk-item-main bk-item-open"
                    title={r.state === 'ready' ? 'Restore this backup' : 'Still uploading'}
                    aria-expanded={restoring === r.backup_id}
                    disabled={r.state !== 'ready'}
                    onClick={() => {
                      setSharing(null)
                      setRestoring(restoring === r.backup_id ? null : r.backup_id)
                    }}
                  >
                    {exactly(r.created_at)}
                    <span className="bk-item-sub">
                      {r.state === 'ready' ? formatBytes(r.packed_size) : 'uploading…'}
                      {' · '}
                      {when(r.created_at)}
                      {r.state === 'ready' && ' · restore…'}
                    </span>
                  </button>
                  {r.state === 'ready' && (
                    <button
                      type="button"
                      className="bk-del bk-share"
                      aria-label={`Share the backup from ${exactly(r.created_at)} with a friend`}
                      title="Share with a friend"
                      aria-expanded={sharing === r.backup_id}
                      onClick={() => {
                        setRestoring(null)
                        setSharing(sharing === r.backup_id ? null : r.backup_id)
                      }}
                    >
                      <Icon name="group" size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="bk-del"
                    aria-label={`Delete the backup from ${exactly(r.created_at)}`}
                    title="Delete this backup"
                    onClick={() => setConfirming(r.backup_id)}
                  >
                    <Icon name="delete" size={14} />
                  </button>
                </>
              )}
            </li>
          ))}
          {g.rows
            .filter((r) => r.backup_id === restoring)
            .map((r) => (
              <li key={`restore-${r.backup_id}`} className="bk-restore-wrap">
                <RestoreForm backup={r} onClose={() => setRestoring(null)} {...(onOpenAgent ? { onOpenAgent } : {})} />
              </li>
            ))}
          {g.rows
            .filter((r) => r.backup_id === sharing)
            .map((r) => (
              <li key={`share-${r.backup_id}`} className="bk-restore-wrap">
                <ShareForm backup={r} onClose={() => setSharing(null)} />
              </li>
            ))}
          {del.isError && <li className="more-note more-quiet">Could not delete it: {String((del.error as Error)?.message)}</li>}
        </ul>
      </StagePage>
    )
  }

  return (
    <StagePage
      {...chrome}
      title="Backups"
      sub={agentCount > 0 ? `${agentCount} agent${agentCount === 1 ? '' : 's'} backed up` : undefined}
      backLabel="Back"
      className="more-page"
    >

      {list.isLoading && <p className="more-note">Reading your backups…</p>}
      {list.isError && (
        <p className="more-note more-quiet">Could not read your backups: {String((list.error as Error)?.message ?? list.error)}</p>
      )}

      {incoming.length > 0 && (
        <div className="bk-shares" aria-label="Shared with you">
          <p className="bk-shares-hd">Shared with you</p>
          {incoming.map((sh) => {
            const from = sh.from_name || sh.from_email || 'A friend'
            const days = Math.max(1, Math.ceil((sh.expires_at - Date.now() / 1000) / 86400))
            const busy = answer.isPending && answer.variables?.id === sh.share_id
            return (
              <div key={sh.share_id} className="bk-share-card">
                <p className="bk-share-text">
                  <span>
                    <b>{from}</b> wants to share <b>{sh.backup.session}</b> with you
                  </span>
                  <span className="bk-item-sub">
                    {formatBytes(sh.backup.packed_size)} · backed up {when(sh.backup.created_at)} · expires in {days}d
                  </span>
                </p>
                <p className="sm-hint">Accepting copies it into your backups, encrypted to your vault; restore it to any of your machines.</p>
                {shareErr[sh.share_id] && (
                  <p className="err-line" role="alert">
                    {shareErr[sh.share_id]}
                  </p>
                )}
                <div className="bk-item-acts">
                  <button type="button" className="sheet-btn" disabled={busy} onClick={() => answer.mutate({ id: sh.share_id, how: 'decline' })}>
                    Decline
                  </button>
                  <button type="button" className="sheet-btn primary" disabled={busy} onClick={() => answer.mutate({ id: sh.share_id, how: 'accept' })}>
                    {busy && answer.variables?.how === 'accept' ? 'Copying…' : 'Accept'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {outgoing.length > 0 && (
        <div className="bk-shares" aria-label="Waiting for a friend">
          <p className="bk-shares-hd">Waiting for a friend</p>
          {outgoing.map((sh) => (
            <div key={sh.share_id} className="bk-item">
              <span className="bk-item-main">
                {sh.session} → {sh.to_name || 'a friend'}
                <span className="bk-item-sub">sent {when(sh.created_at)} · not accepted yet</span>
              </span>
              <span className="bk-item-acts">
                <button
                  type="button"
                  className="sheet-btn"
                  disabled={answer.isPending && answer.variables?.id === sh.share_id}
                  onClick={() => answer.mutate({ id: sh.share_id, how: 'cancel' })}
                >
                  Cancel
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && agentCount === 0 && incoming.length === 0 && (
        <p className="more-note">
          No backups yet. Open an agent’s card — tap its face — and choose <b>Backup this agent</b>. It
          is encrypted on its machine, to a vault only you can open.
        </p>
      )}

      {machines.map((m) => {
        const id = `more-bk:${m.deviceId}`
        return (
          <SidebarSection key={m.deviceId} id={id} label={m.name} count={m.agents.length} isOpen={machineOpen(id)} onToggle={toggleMachine}>
              {m.agents.map((g) => {
                const newest = g.rows[0]!
                const ready = g.rows.filter((r) => r.state === 'ready')
                return (
                  <button
                    key={g.key}
                    type="button"
                    className="sb-row art-row bk-agent"
                    onClick={() => setOpen(g.key)}
                    title={`${g.session} on ${m.name} — newest backup ${exactly(newest.created_at)}`}
                  >
                    <span className={`art-dot ${newest.state === 'ready' ? 'art-ok' : 'art-wait'}`} aria-hidden="true" />
                    <span className="art-row-main">
                      <span className="art-row-name">{g.session}</span>
                      <span className="art-row-sub">
                        {ready.length} backup{ready.length === 1 ? '' : 's'}
                        {' · '}
                        {newest.state === 'ready' ? `latest ${when(newest.created_at)}` : 'backing up now'}
                      </span>
                    </span>
                    <Icon name="chevron_right" size={16} />
                  </button>
                )
              })}
          </SidebarSection>
        )
      })}

      {list.isSuccess && policy && (rows.length > 0 || hasVault) && (
        <p className="more-note bk-usage">
          {formatBytes(list.data.used_bytes)} of {formatBytes(policy.account_bytes)} used · each agent keeps its newest{' '}
          {policy.keep_per_agent}
        </p>
      )}

      {said && <p className="more-note">{said}</p>}

      {hasVault && door === null && (
        <p className="more-note bk-vault-links">
          <button type="button" className="bk-link" onClick={() => setDoor('recover')}>
            Forgot your vault password?
          </button>
          <button type="button" className="bk-link bk-link-danger" onClick={() => setDoor('reset')}>
            Reset vault…
          </button>
        </p>
      )}
      <div ref={doorRef} className="bk-door">
      {hasVault && door === 'recover' && vault.data?.vault && (
        <VaultRecover
          vault={vault.data.vault}
          onBack={() => setDoor(null)}
          onLostBoth={() => setDoor('reset')}
          onDone={() => {
            setDoor(null)
            setSaid('Your new password is set. Your backups are kept.')
            void qc.invalidateQueries({ queryKey: ['backup-vault'] })
          }}
        />
      )}
      {hasVault && door === 'reset' && (
        <VaultReset
          onBack={() => setDoor(null)}
          onDone={(deleted) => {
            setDoor(null)
            setSaid(
              `Vault reset — ${deleted} backup${deleted === 1 ? '' : 's'} deleted. Your next backup sets up a new vault.`,
            )
            void qc.invalidateQueries({ queryKey: ['backup-vault'] })
            void qc.invalidateQueries({ queryKey: ['backups'] })
          }}
        />
      )}
      </div>
    </StagePage>
  )
}
